import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import BaseSelectField from '../common/SelectField.js';
import SearchSubmitInput from '../common/SearchSubmitInput.js';
import {
  filterInputClass,
  filterDateInputClass,
  selectDropdownOpenClass,
} from '../common/uiClasses.js';
import userService from '../../services/UserService.js';
import vehicleService from '../../services/vehicleService.js';
import { locationHubService } from '../../services/locationHubService.js';
import shipmentClientService from '../../services/ShipmentClientService.js';
import externalCommissionService from '../../services/ExternalCommissionService.js';
import expenseService from '../../services/ExpenseService.js';
import contractPartyService from '../../services/contractPartyService.js';
import {
  formatHubBranchLabel,
  formatUserWithLocation,
  formatUserNameRoleHub,
  filterShipmentLocations,
  destinationTypeLabel,
  isHubDestination,
  isBranchDestination,
} from '../../utils/destinationType.js';
import {
  REPORT_DEFS,
  getTodayIso,
  isReportVisible,
} from './reportConfig.js';
import { formatFinancialCell, isFinancialColumnLabel } from '../../utils/formatCurrency.js';

/** Same label / trigger styles as Shipment list filters */
const filterLabelClass = 'block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2';
const filterTriggerClass =
  'w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl cursor-pointer flex justify-between items-center bg-white text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-400';

/** Equal-width cell wrapper used by every filter field */
const FilterField = ({ children, className = '' }) => (
  <div className={`min-w-0 w-full max-w-full ${className}`}>{children}</div>
);

const SHIPMENT_ID_COLUMNS = new Set([
  'shipment id',
  'gc number',
  'gc no',
  'gc',
]);

const ML_SHEET_ID_COLUMNS = new Set([
  'ml sheet',
  'ml sheet id',
  'dispatch id',
  'ogcl',
  'ogcl number',
  'ogcl no',
  'ogcl id',
]);

const isShipmentIdColumn = (col) => SHIPMENT_ID_COLUMNS.has(String(col || '').trim().toLowerCase());
const isMlSheetIdColumn = (col) => ML_SHEET_ID_COLUMNS.has(String(col || '').trim().toLowerCase());

const TOTAL_LABELS = {
  shipments: 'Total Shipments',
  total_amount: 'Total Amount',
  other_charges: 'Other',
  on_account_amount: 'On Account',
  booked: 'Booked',
  dispatched: 'Dispatched',
  in_transit: 'In Transit',
  received: 'Received',
  delivered: 'Delivered',
  returned: 'Returned',
  rejected: 'Cancelled',
  articles: 'Articles',
  qty: 'Qty',
  freight: 'Freight',
  loading: 'Loading',
  unloading: 'Unloading',
  handling: 'Handling',
  docket: 'Docket',
  door_pickup: 'Door Pickup',
  door_delivery: 'Door Delivery',
  toll: 'Toll',
  fuel_surcharge: 'Fuel Surcharge',
  packing: 'Packing',
  insurance: 'Insurance',
  other: 'Other',
  late: 'Late',
  discount: 'Discount',
  gst: 'GST',
  total: 'Total Amt',
  paid: 'Paid',
  balance: 'Balance',
  collected: 'Collected cash',
  paid_count: 'Paid GCs',
  paid_amount: 'Paid bookings (Rs.)',
  to_pay_count: 'To-Pay GCs',
  to_pay_amount: 'To-Pay (Rs.)',
  credit_count: 'Credit GCs',
  credit_amount: 'Credit (Rs.)',
  itcl_created: 'ITCL Created',
  tds: 'TDS',
  outstanding: 'Outstanding',
  'booking outstanding': 'Booking outstanding',
  'delivery outstanding': 'Delivery outstanding',
  'payable / receivable': 'Payable / Receivable',
  'yesterday cumulative': 'Yesterday Cumulative',
};

const formatTotalLabel = (key) =>
  TOTAL_LABELS[key] || String(key).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const formatTotalValue = (key, value, map = {}) => {
  if (key === 'outstanding' || key === 'yesterday cumulative' || String(key).endsWith('outstanding')) {
    const n = Number(value);
    if (Number.isFinite(n)) {
      const side = key === 'outstanding'
        ? (map['payable / receivable'] || (n >= 0 ? 'Receivable' : 'Payable'))
        : (n >= 0 ? 'Receivable' : 'Payable');
      const formatted = formatFinancialCell(key, Math.abs(n));
      return `${side} ${formatted ?? Math.abs(n).toLocaleString('en-IN')}`;
    }
  }
  if (value == null || value === '') return '—';
  if (isFinancialColumnLabel(key)) {
    const formatted = formatFinancialCell(key, value);
    return formatted == null || formatted === '' ? '—' : formatted;
  }
  if (typeof value === 'number') {
    return Number(value).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }
  return String(value);
};

const TOTAL_KEY_ORDER = [
  'records', 'shipments', 'paid_amount', 'total_amount', 'to_pay_amount', 'on_account_amount', 'other_charges',
  'booked', 'dispatched', 'itcl_created', 'in_transit', 'received', 'delivered', 'returned', 'rejected',
  'articles', 'qty', 'freight', 'loading', 'unloading', 'handling', 'docket',
  'door_pickup', 'door_delivery', 'toll', 'fuel_surcharge', 'packing', 'insurance',
  'other', 'late', 'discount', 'gst', 'total', 'paid', 'balance', 'collected',
  'paid_count', 'paid_amount', 'to_pay_count', 'to_pay_amount', 'credit_count', 'credit_amount',
  'booking commission', 'delivery commission',
  'tds', 'payment received', 'yesterday cumulative', 'outstanding',
  'booking outstanding', 'delivery outstanding',
];

const HIDDEN_TOTAL_KEYS = new Set(['payable / receivable']);

export const TotalsSummaryTable = ({ totals, recordCount }) => {
  if (!totals || typeof totals !== 'object') return null;
  const entries = Object.entries(totals).filter(([, v]) => v == null || ['string', 'number', 'boolean'].includes(typeof v));
  if (!entries.length && recordCount == null) return null;
  const map = Object.fromEntries(entries);
  const orderedKeys = [
    ...(recordCount != null && recordCount !== '' ? ['records'] : []),
    ...TOTAL_KEY_ORDER.filter((k) => k !== 'records' && k in map),
    ...entries.map(([k]) => k).filter((k) => !TOTAL_KEY_ORDER.includes(k) && !HIDDEN_TOTAL_KEYS.has(k)),
  ].filter((k) => !HIDDEN_TOTAL_KEYS.has(k));
  const rows = orderedKeys.map((k) => [k, k === 'records' ? recordCount : map[k]]);
  return (
    <div className="px-3 sm:px-4 py-2.5 border-b border-slate-200">
      <div className="lg:hidden grid grid-cols-2 gap-1.5">
        {rows.map(([k, v]) => (
          <div key={k} className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 min-w-0">
            <p className="text-[10px] font-semibold text-slate-500 truncate">{formatTotalLabel(k)}</p>
            <p className="text-xs font-bold text-slate-800 truncate">{formatTotalValue(k, v, map)}</p>
          </div>
        ))}
      </div>
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full border-collapse text-[11px] sm:text-xs min-w-[320px]">
          <thead>
            <tr>
              {rows.map(([k]) => (
                <th
                  key={k}
                  className="text-left px-2 py-1.5 whitespace-nowrap font-bold border border-sky-200"
                  style={{ background: '#D6E3F8', color: '#1F4E79' }}
                >
                  {formatTotalLabel(k)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr style={{ background: '#F8FAFC' }}>
              {rows.map(([k, v]) => (
                <td key={k} className="px-2 py-1.5 border border-slate-200 whitespace-nowrap font-semibold text-slate-800">
                  {formatTotalValue(k, v, map)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const AgentBreakdownTable = ({ breakdown }) => {
  const columns = breakdown?.columns || [];
  const rows = breakdown?.rows || [];
  if (!columns.length || !rows.length) return null;
  return (
    <div className="px-3 sm:px-4 py-2.5 border-b border-slate-200">
      <p className="text-[11px] font-bold text-slate-700 mb-1.5">Agents on this hub / branch</p>
      <div className="lg:hidden space-y-2">
        {rows.map((row, idx) => {
          const cells = Array.isArray(row) ? row : columns.map((c) => row[c]);
          return (
            <div key={idx} className="rounded-lg border border-slate-200 bg-white p-2">
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                {columns.map((col, cidx) => (
                  <div key={col} className="min-w-0">
                    <p className="text-[10px] font-semibold text-slate-400 truncate">{col}</p>
                    <p className="text-[11px] font-medium text-slate-800 break-words">
                      {cells[cidx] == null || cells[cidx] === '' ? '—' : (formatFinancialCell(col, cells[cidx]) ?? '—')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="hidden lg:block overflow-x-auto">
      <table className="w-full border-collapse text-[11px] sm:text-xs min-w-[480px]">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col}
                className="text-left px-2 py-1.5 whitespace-nowrap font-bold border border-sky-200"
                style={{ background: '#E8F0FE', color: '#1F4E79' }}
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx} style={{ background: idx % 2 ? '#F8FAFC' : '#fff' }}>
              {(Array.isArray(row) ? row : columns.map((c) => row[c])).map((cell, cidx) => (
                <td key={cidx} className="px-2 py-1.5 border border-slate-200 whitespace-nowrap">
                  {cell == null || cell === '' ? '—' : (formatFinancialCell(columns[cidx], cell) ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
};

export const ReportCell = ({ col, cell, onOpenShipment, onOpenMlSheet }) => {
  if (cell == null || cell === '') return '—';
  const text = isFinancialColumnLabel(col)
    ? String(formatFinancialCell(col, cell) ?? cell)
    : String(cell);
  const displayText = isStatusColumn(col) && text.trim().toLowerCase() === 'rejected'
    ? 'Cancelled'
    : text;
  if (isShipmentIdColumn(col) && text && text !== '—') {
    return (
      <button
        type="button"
        onClick={() => onOpenShipment?.(text)}
        className="text-blue-700 font-semibold underline underline-offset-2 hover:text-blue-900"
      >
        {text}
      </button>
    );
  }
  if (isMlSheetIdColumn(col) && text && text !== '—') {
    return (
      <button
        type="button"
        onClick={() => onOpenMlSheet?.(text)}
        className="text-blue-700 font-semibold underline underline-offset-2 hover:text-blue-900"
      >
        {text}
      </button>
    );
  }
  return displayText;
};

const isStatusColumn = (col) => /status/i.test(String(col || ''));

export const ReportRecordCard = ({ columns, cells, onOpenShipment, onOpenMlSheet }) => {
  const statusIdx = columns.findIndex(isStatusColumn);
  const primaryIdx = 0;
  const detailCols = columns
    .map((col, idx) => ({ col, idx }))
    .filter(({ idx }) => idx !== primaryIdx && idx !== statusIdx);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-2.5">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold text-slate-400 truncate">{columns[primaryIdx]}</p>
          <div className="text-sm font-bold text-slate-800 break-all leading-snug">
            <ReportCell col={columns[primaryIdx]} cell={cells[primaryIdx]} onOpenShipment={onOpenShipment} onOpenMlSheet={onOpenMlSheet} />
          </div>
        </div>
        {statusIdx >= 0 && (
          <span className="shrink-0 max-w-[42%] rounded-full bg-slate-100 border border-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-700 text-right leading-snug">
            <ReportCell col={columns[statusIdx]} cell={cells[statusIdx]} onOpenShipment={onOpenShipment} onOpenMlSheet={onOpenMlSheet} />
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
        {detailCols.map(({ col, idx }) => (
          <div key={`${col}-${idx}`} className="min-w-0">
            <p className="text-[10px] font-semibold text-slate-400 truncate">{col}</p>
            <div className="text-[11px] font-medium text-slate-800 break-words leading-snug">
              <ReportCell col={col} cell={cells[idx]} onOpenShipment={onOpenShipment} onOpenMlSheet={onOpenMlSheet} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ReportSelect = ({ value, onChange, placeholder, options = [], label }) => (
  <FilterField>
    <BaseSelectField
      variant="filter"
      label={label}
      labelClassName={filterLabelClass}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      emptyLabel={placeholder}
      options={[
        { value: '', label: placeholder || 'All' },
        ...(options || []).map((opt) => ({ value: opt.value, label: opt.label })),
      ]}
    />
  </FilterField>
);

const TextInput = ({ value, onChange, placeholder, label, submit = false }) => (
  <FilterField>
    {label && <label className={filterLabelClass}>{label}</label>}
    {submit ? (
      <SearchSubmitInput
        value={value || ''}
        onSearch={onChange}
        placeholder={placeholder}
        ariaLabel={label}
        inputClassName={filterInputClass}
      />
    ) : (
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={filterInputClass}
      />
    )}
  </FilterField>
);

const DateRangeRow = ({ fromValue, toValue, onFromChange, onToChange, label }) => (
  <FilterField className="col-span-2">
    {label && <label className={filterLabelClass}>{label}</label>}
    <div className="flex flex-row items-stretch gap-1.5 w-full min-w-0">
      <input
        type="date"
        value={fromValue || ''}
        onChange={(e) => onFromChange(e.target.value)}
        className={`${filterDateInputClass} flex-1 min-w-0`}
        aria-label="From date"
      />
      <input
        type="date"
        value={toValue || ''}
        onChange={(e) => onToChange(e.target.value)}
        className={`${filterDateInputClass} flex-1 min-w-0`}
        aria-label="To date"
      />
    </div>
  </FilterField>
);

const CheckboxDropdown = ({ label, options, selected = [], onChange, emptyLabel, locked = false }) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  const count = selected.length;

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selectedLabels = (options || [])
    .filter((o) => selected.includes(o.value))
    .map((o) => o.label);

  return (
    <FilterField>
      <div ref={ref} className={`relative w-full ${open ? 'z-[80]' : ''}`}>
        <label className={filterLabelClass}>{label}</label>
        <div
          role="button"
          tabIndex={locked ? -1 : 0}
          onClick={() => {
            if (locked) return;
            setOpen((v) => !v);
          }}
          onKeyDown={(e) => {
            if (locked) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setOpen((v) => !v);
            }
          }}
          className={`${filterTriggerClass} ${open ? selectDropdownOpenClass : ''} ${locked ? 'bg-slate-50 cursor-not-allowed' : ''}`}
        >
          <span className={`min-w-0 flex-1 truncate pr-2 ${count ? 'text-gray-800' : 'text-gray-400'}`}>
            {count ? selectedLabels.join(', ') : (emptyLabel || `All ${label}`)}
          </span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${open ? 'rotate-180 text-blue-500' : ''}`}
            aria-hidden="true"
          />
        </div>
        {open && !locked && (
          <div
            className="absolute z-50 top-full left-0 w-full bg-white border border-gray-300 rounded-xl shadow-lg mt-1"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="max-h-48 overflow-y-auto">
              {count > 0 && (
                <div
                  onClick={() => onChange([])}
                  className="px-3 py-2 text-xs text-blue-600 font-semibold hover:bg-blue-50 cursor-pointer border-b border-gray-100"
                >
                  Clear selection
                </div>
              )}
              {(options || []).map(({ value, label: optLabel }) => {
                const checked = selected.includes(value);
                return (
                  <label
                    key={value}
                    className="flex items-center gap-2 px-3 sm:px-4 py-2 hover:bg-blue-50 cursor-pointer text-sm sm:text-base text-gray-800"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        onChange(
                          checked
                            ? selected.filter((x) => x !== value)
                            : [...selected, value],
                        );
                      }}
                      className="accent-blue-500"
                    />
                    {optLabel}
                  </label>
                );
              })}
              {(options || []).length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-400">No options</div>
              )}
            </div>
          </div>
        )}
      </div>
    </FilterField>
  );
};

/** Per-hub mode: none | all (hub+branches) | hub-only | partial (named branches). */
const modesFromHubFilters = (groups, hubIds, childIds, includeChildren) => {
  const hubSet = new Set((hubIds || []).map(String).filter(Boolean));
  const childSet = new Set((childIds || []).map(String).filter(Boolean));
  return (groups || []).map((g) => {
    const id = String(g.value);
    const kids = (g.children || []).map((c) => String(c.value));
    const parentIn = hubSet.has(id);
    const kidsInHub = kids.filter((k) => hubSet.has(k));
    const kidsInChild = kids.filter((k) => childSet.has(k));
    const selectedChildren = [...new Set([...kidsInChild, ...kidsInHub])];
    if (!kids.length) {
      return { id, mode: parentIn ? 'all' : 'none', selectedChildren: [] };
    }
    if (
      parentIn
      && includeChildren === false
      && !selectedChildren.length
    ) {
      return { id, mode: 'hub-only', selectedChildren: [] };
    }
    if (parentIn && includeChildren !== false && childSet.size === 0 && !kidsInHub.length) {
      return { id, mode: 'all', selectedChildren: kids };
    }
    if (selectedChildren.length) {
      if (
        selectedChildren.length === kids.length
        && parentIn
        && includeChildren !== false
        && childSet.size === 0
      ) {
        return { id, mode: 'all', selectedChildren: kids };
      }
      return { id, mode: 'partial', selectedChildren };
    }
    if (parentIn) return { id, mode: 'hub-only', selectedChildren: [] };
    return { id, mode: 'none', selectedChildren: [] };
  });
};

const filtersFromHubModes = (groups, modes) => {
  const byId = Object.fromEntries((modes || []).map((m) => [String(m.id), m]));
  const active = [];
  (groups || []).forEach((g) => {
    const m = byId[String(g.value)];
    if (!m || m.mode === 'none') return;
    const hasKids = (g.children || []).length > 0;
    active.push({
      id: String(g.value),
      mode: hasKids ? m.mode : 'leaf',
      selectedChildren: m.selectedChildren || [],
      childIds: (g.children || []).map((c) => String(c.value)),
    });
  });
  if (!active.length) {
    return { location_hub_ids: [], include_child_branches: true, child_location_hub_ids: [] };
  }
  const nonLeaf = active.filter((a) => a.mode !== 'leaf');
  const ids = active.map((a) => a.id);
  if (!nonLeaf.length || nonLeaf.every((a) => a.mode === 'all')) {
    return { location_hub_ids: ids, include_child_branches: true, child_location_hub_ids: [] };
  }
  if (nonLeaf.every((a) => a.mode === 'hub-only')) {
    return { location_hub_ids: ids, include_child_branches: false, child_location_hub_ids: [] };
  }
  if (nonLeaf.every((a) => a.mode === 'partial')) {
    return {
      location_hub_ids: ids,
      include_child_branches: true,
      child_location_hub_ids: active.flatMap((a) => a.selectedChildren),
    };
  }
  const location_hub_ids = [];
  active.forEach((a) => {
    if (a.mode === 'all' || a.mode === 'leaf') {
      location_hub_ids.push(a.id, ...a.childIds);
    } else if (a.mode === 'hub-only') {
      location_hub_ids.push(a.id);
    } else {
      location_hub_ids.push(...a.selectedChildren);
    }
  });
  return {
    location_hub_ids: [...new Set(location_hub_ids)],
    include_child_branches: false,
    child_location_hub_ids: [],
  };
};

const NestedHubDropdown = ({
  label,
  emptyLabel = 'All Hubs',
  groups = [],
  selectedHubIds = [],
  selectedChildIds = [],
  includeChildren = true,
  onChange,
  locked = false,
}) => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const ref = React.useRef(null);
  const parentLockedRef = React.useRef(new Set());
  const modes = modesFromHubFilters(groups, selectedHubIds, selectedChildIds, includeChildren);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setExpanded((prev) => {
      const n = new Set(prev);
      modesFromHubFilters(groups, selectedHubIds, selectedChildIds, includeChildren).forEach((m) => {
        if (m.mode !== 'none') n.add(String(m.id));
      });
      return n;
    });
    // Only when the menu opens so users can still collapse a hub.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const modeById = Object.fromEntries(modes.map((m) => [String(m.id), m]));
  const selectedLabels = [];
  groups.forEach((g) => {
    const m = modeById[String(g.value)];
    if (!m || m.mode === 'none') return;
    if (m.mode === 'all' || m.mode === 'hub-only' || !(g.children || []).length) {
      selectedLabels.push(g.label);
      return;
    }
    (g.children || []).forEach((c) => {
      if (m.selectedChildren.includes(String(c.value))) {
        selectedLabels.push(`${g.label} › ${c.label}`);
      }
    });
  });
  const count = selectedLabels.length;

  const commitModes = (nextModes) => {
    onChange(filtersFromHubModes(groups, nextModes));
  };

  const toggleParent = (group) => {
    const id = String(group.value);
    const next = modes.map((m) => ({ ...m, selectedChildren: [...(m.selectedChildren || [])] }));
    const idx = next.findIndex((m) => m.id === id);
    if (idx < 0) return;
    const kids = (group.children || []).map((c) => String(c.value));
    const cur = next[idx];
    if (cur.mode === 'all') {
      next[idx] = { id, mode: 'none', selectedChildren: [] };
      parentLockedRef.current.delete(id);
    } else if (cur.mode === 'hub-only') {
      next[idx] = { id, mode: kids.length ? 'all' : 'none', selectedChildren: kids };
      if (kids.length) parentLockedRef.current.add(id);
      else parentLockedRef.current.delete(id);
    } else {
      next[idx] = { id, mode: 'all', selectedChildren: kids };
      parentLockedRef.current.add(id);
    }
    if (kids.length && next[idx].mode !== 'none') {
      setExpanded((prev) => {
        const n = new Set(prev);
        n.add(id);
        return n;
      });
    }
    commitModes(next);
  };

  const toggleChild = (group, childId) => {
    const id = String(group.value);
    const cid = String(childId);
    const next = modes.map((m) => ({ ...m, selectedChildren: [...(m.selectedChildren || [])] }));
    const idx = next.findIndex((m) => m.id === id);
    if (idx < 0) return;
    const kids = (group.children || []).map((c) => String(c.value));
    const cur = next[idx];
    let selected = cur.mode === 'all' ? [...kids] : [...(cur.selectedChildren || [])];
    if (selected.includes(cid)) selected = selected.filter((x) => x !== cid);
    else selected = [...selected, cid];
    if (!selected.length) {
      const keepHub = parentLockedRef.current.has(id) || cur.mode === 'all' || cur.mode === 'hub-only';
      next[idx] = { id, mode: keepHub ? 'hub-only' : 'none', selectedChildren: [] };
      if (!keepHub) parentLockedRef.current.delete(id);
    } else if (selected.length === kids.length) {
      next[idx] = { id, mode: 'all', selectedChildren: kids };
      parentLockedRef.current.add(id);
    } else {
      next[idx] = { id, mode: 'partial', selectedChildren: selected };
    }
    if (kids.length) {
      setExpanded((prev) => {
        const n = new Set(prev);
        n.add(id);
        return n;
      });
    }
    commitModes(next);
  };

  return (
    <FilterField>
      <div ref={ref} className={`relative w-full ${open ? 'z-[80]' : ''}`}>
        <label className={filterLabelClass}>{label}</label>
        <div
          role="button"
          tabIndex={locked ? -1 : 0}
          onClick={() => {
            if (locked) return;
            setOpen((v) => !v);
          }}
          onKeyDown={(e) => {
            if (locked) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setOpen((v) => !v);
            }
          }}
          className={`${filterTriggerClass} ${open ? selectDropdownOpenClass : ''} ${locked ? 'bg-slate-50 cursor-not-allowed' : ''}`}
        >
          <span className={`min-w-0 flex-1 truncate pr-2 ${count ? 'text-gray-800' : 'text-gray-400'}`}>
            {count ? selectedLabels.join(', ') : emptyLabel}
          </span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${open ? 'rotate-180 text-blue-500' : ''}`}
            aria-hidden="true"
          />
        </div>
        {open && !locked && (
          <div
            className="absolute z-50 top-full left-0 w-full bg-white border border-gray-300 rounded-xl shadow-lg mt-1"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="max-h-72 overflow-y-auto">
              {count > 0 && (
                <div
                  onClick={() => {
                    parentLockedRef.current = new Set();
                    onChange({
                      location_hub_ids: [],
                      include_child_branches: true,
                      child_location_hub_ids: [],
                    });
                  }}
                  className="px-3 py-2 text-xs text-blue-600 font-semibold hover:bg-blue-50 cursor-pointer border-b border-gray-100"
                >
                  Clear selection
                </div>
              )}
              {groups.map((group) => {
                const m = modeById[String(group.value)] || { mode: 'none', selectedChildren: [] };
                const kids = group.children || [];
                const parentChecked = m.mode === 'all' || m.mode === 'hub-only';
                const parentIndeterminate = m.mode === 'partial';
                const isOpen = expanded.has(String(group.value));
                return (
                  <div key={group.value} className="border-b border-gray-50 last:border-b-0">
                    <div className="flex items-center gap-1 px-2 sm:px-3 py-1.5 hover:bg-blue-50 text-sm sm:text-base text-gray-800">
                      {kids.length > 0 ? (
                        <button
                          type="button"
                          aria-label={isOpen ? 'Collapse branches' : 'Expand branches'}
                          onClick={() => {
                            setExpanded((prev) => {
                              const n = new Set(prev);
                              const key = String(group.value);
                              if (n.has(key)) n.delete(key);
                              else n.add(key);
                              return n;
                            });
                          }}
                          className="p-0.5 rounded text-gray-500 hover:text-blue-600 shrink-0"
                        >
                          <ChevronRight className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                        </button>
                      ) : (
                        <span className="w-5 shrink-0" />
                      )}
                      <label className="flex items-center gap-2 min-w-0 flex-1 cursor-pointer py-0.5">
                        <input
                          type="checkbox"
                          checked={parentChecked}
                          ref={(el) => {
                            if (el) el.indeterminate = parentIndeterminate;
                          }}
                          onChange={() => toggleParent(group)}
                          className="accent-blue-500 shrink-0"
                        />
                        <span className="truncate">{group.label}</span>
                      </label>
                    </div>
                    {kids.length > 0 && isOpen && kids.map((child) => {
                      const checked = m.mode === 'all' || (m.selectedChildren || []).includes(String(child.value));
                      return (
                        <label
                          key={child.value}
                          className="flex items-center gap-2 pl-10 pr-3 py-1.5 hover:bg-blue-50 cursor-pointer text-sm text-gray-700"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleChild(group, child.value)}
                            className="accent-blue-500"
                          />
                          <span className="truncate">{group.label} › {child.label}</span>
                        </label>
                      );
                    })}
                  </div>
                );
              })}
              {groups.length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-400">No options</div>
              )}
            </div>
          </div>
        )}
      </div>
    </FilterField>
  );
};

export function useReportFilterCatalog({ type, user, filters, setFilters }) {
  const def = REPORT_DEFS[type];
  const roleName = user?.role_name || user?.role;
  const isAgentUser = roleName === 'Agent';

  const [users, setUsers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [clients, setClients] = useState([]);
  const [vehicleOwners, setVehicleOwners] = useState([]);
  const [externalAgents, setExternalAgents] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [contractParties, setContractParties] = useState([]);

  useEffect(() => {
    if (!user || !def || !isReportVisible(def, user)) return undefined;
    const toArray = (d) => {
      if (Array.isArray(d)) return d;
      if (!d) return [];
      for (const key of ['users', 'vehicles', 'hubs', 'location_hubs', 'clients', 'data', 'results', 'items']) {
        if (Array.isArray(d[key])) return d[key];
        if (d.data && Array.isArray(d.data[key])) return d.data[key];
      }
      if (Array.isArray(d.data)) return d.data;
      return [];
    };
    userService.getAll(null, null, { all: true }).then((d) => setUsers(toArray(d))).catch(() => setUsers([]));
    vehicleService.getAllVehicles(null, null, { all: true }).then((d) => setVehicles(toArray(d))).catch(() => setVehicles([]));
    locationHubService.getAllHubs(null, null, { all: true })
      .then((d) => setLocations(toArray(d?.data ?? d)))
      .catch(() => setLocations([]));
    shipmentClientService.getAll(null, null, { all: true }).then((d) => setClients(toArray(d))).catch(() => setClients([]));
    if (type === 'expense' || type === 'approved-transactions') {
      expenseService.getCategories()
        .then((res) => {
          const list = toArray(res?.data ?? res);
          setExpenseCategories(
            list.map((c) => ({
              value: c.value || c.id || c,
              label: c.label || String(c.value || c).replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase()),
            })),
          );
        })
        .catch(() => setExpenseCategories([]));
    }
    if (type === 'external-commission') {
      externalCommissionService.getVehicleOwners()
        .then((res) => setVehicleOwners(toArray(res?.data?.data ?? res?.data ?? res)))
        .catch(() => setVehicleOwners([]));
      externalCommissionService.loadBulkSettlementAgents()
        .then((agents) => setExternalAgents(agents || []))
        .catch(() => setExternalAgents([]));
    }
    if (type === 'commission') {
      contractPartyService.getAllUnpaginated()
        .then((res) => {
          const list = toArray(res?.data ?? res);
          setContractParties(list);
        })
        .catch(() => setContractParties([]));
    }
    return undefined;
  }, [user, def, type]);

  const hubsById = useMemo(() => {
    const map = {};
    (locations || []).forEach((l) => {
      if (l?.id != null) {
        map[l.id] = l;
        map[String(l.id)] = l;
      }
    });
    return map;
  }, [locations]);

  const locationLabel = (l) => formatHubBranchLabel(l, hubsById) || l?.location || l?.hub_name || l?.name || '';
  const hubOptionLabel = (l) => {
    const name = locationLabel(l);
    const kind = destinationTypeLabel(l?.destination_type);
    return kind ? `${name} (${kind})` : name;
  };
  const allShipmentLocations = useMemo(
    () => filterShipmentLocations(locations || []),
    [locations]
  );
  const roleScopedLocations = useMemo(() => {
    const mineId = user?.location_hub_id != null ? String(user.location_hub_id) : '';
    if ((roleName === 'Hub Admin' || roleName === 'Agent') && mineId) {
      const mine = allShipmentLocations.find((l) => String(l.id) === mineId);
      const allowed = new Set([mineId]);
      if (mine && isHubDestination(mine.destination_type)) {
        allShipmentLocations.forEach((l) => {
          if (String(l.parent_hub_id) === mineId) allowed.add(String(l.id));
        });
      }
      return allShipmentLocations.filter((l) => allowed.has(String(l.id)));
    }
    return allShipmentLocations;
  }, [allShipmentLocations, roleName, user?.location_hub_id]);

  const hubTreeGroups = useMemo(() => {
    const parents = roleScopedLocations.filter((l) => {
      if (isHubDestination(l.destination_type) || !l.parent_hub_id) return true;
      return !roleScopedLocations.some((p) => String(p.id) === String(l.parent_hub_id));
    });
    return parents
      .map((l) => {
        const value = l.id != null ? String(l.id) : '';
        const children = roleScopedLocations
          .filter((c) => isBranchDestination(c.destination_type) && String(c.parent_hub_id) === value)
          .map((c) => ({
            value: c.id != null ? String(c.id) : '',
            label: locationLabel(c),
          }))
          .filter((o) => o.value);
        return { value, label: hubOptionLabel(l), children };
      })
      .filter((o) => o.value);
  }, [roleScopedLocations, hubsById]);

  const flatBranchOptions = useMemo(() => {
    const options = [];
    const seen = new Set();
    hubTreeGroups.forEach((group) => {
      const children = group.children || [];
      if (children.length) {
        children.forEach((child) => {
          if (!child.value || seen.has(child.value)) return;
          seen.add(child.value);
          options.push({ value: child.value, label: child.label });
        });
        return;
      }
      if (!group.value || seen.has(group.value)) return;
      seen.add(group.value);
      options.push({ value: group.value, label: String(group.label || '').replace(/\s*\([^)]*\)\s*$/, '') });
    });
    return options;
  }, [hubTreeGroups]);

  const locationOptions = roleScopedLocations.map((l) => ({
    value: locationLabel(l) || l.location,
    label: locationLabel(l),
  }));
  const stationOptions = roleScopedLocations.map((l) => ({
    value: l.id != null ? String(l.id) : '',
    label: hubOptionLabel(l) || locationLabel(l),
  })).filter((o) => o.value);
  const selectedHubIds = (filters.location_hub_ids || []).map(String).filter(Boolean);
  const includeChildBranches = filters.include_child_branches !== false;
  const selectedChildHubIds = (filters.child_location_hub_ids || []).map(String).filter(Boolean);
  const hubModes = useMemo(
    () => modesFromHubFilters(hubTreeGroups, selectedHubIds, selectedChildHubIds, includeChildBranches),
    [hubTreeGroups, selectedHubIds, selectedChildHubIds, includeChildBranches],
  );
  const effectiveHubIdsForAgents = useMemo(() => {
    const ids = new Set();
    const byId = Object.fromEntries(hubModes.map((m) => [String(m.id), m]));
    hubTreeGroups.forEach((g) => {
      const m = byId[String(g.value)];
      if (!m || m.mode === 'none') return;
      if (m.mode === 'hub-only') {
        ids.add(String(g.value));
        return;
      }
      if (m.mode === 'all') {
        ids.add(String(g.value));
        (g.children || []).forEach((c) => ids.add(String(c.value)));
        return;
      }
      (m.selectedChildren || []).forEach((id) => ids.add(String(id)));
    });
    if (!ids.size) selectedHubIds.forEach((id) => ids.add(id));
    return [...ids];
  }, [hubModes, hubTreeGroups, selectedHubIds]);
  const userOptions = (users || [])
    .filter((u) => u.user_id)
    .map((u) => ({
      value: String(u.user_id),
      label: formatUserWithLocation(u, hubsById),
      locationHubId: u.location_hub_id != null ? String(u.location_hub_id) : '',
    }));
  const hubScopedUserOptions = effectiveHubIdsForAgents.length
    ? userOptions.filter((u) => effectiveHubIdsForAgents.includes(u.locationHubId))
    : userOptions;
  const dailyBranchUserOptions = (users || [])
    .filter((u) => u.user_id)
    .filter((u) => {
      if (!effectiveHubIdsForAgents.length) return true;
      const hid = u.location_hub_id != null ? String(u.location_hub_id) : '';
      return effectiveHubIdsForAgents.includes(hid);
    })
    .map((u) => ({
      value: String(u.user_id),
      label: formatUserNameRoleHub(u, hubsById),
      locationHubId: u.location_hub_id != null ? String(u.location_hub_id) : '',
    }));
  const agentOptions = hubScopedUserOptions.filter((u) => {
    const raw = (users || []).find((x) => String(x.user_id) === String(u.value));
    const role = typeof raw?.role === 'string' ? raw.role : (raw?.role_name || raw?.role?.role_name || '');
    return role === 'Agent';
  });
  const contractPartyOptions = (contractParties || []).map((p) => ({
    value: p.id != null ? String(p.id) : '',
    label: p.name || p.company_name || `Party ${p.id}`,
  }));
  const vehicleOptions = (vehicles || []).map((v) => ({
    value: v.id || v.vehicle_id,
    label: `${v.vehicle_name || v.name || ''} – ${v.vehicle_number || v.number || ''}`,
  }));
  const clientOptions = (clients || []).map((c) => ({
    value: String(c.id || c.client_id || ''),
    label: c.company_name || c.name || '',
  })).filter((o) => o.value);

  const setF = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const setDailyBranchUser = (userId) => {
    const match = (users || []).find((u) => String(u.user_id) === String(userId));
    const hid = match?.location_hub_id != null ? String(match.location_hub_id) : '';
    setFilters((prev) => ({
      ...prev,
      user_id: userId || '',
      ...(hid
        ? {
            location_hub_ids: [hid],
            child_location_hub_ids: [],
            include_child_branches: true,
          }
        : {}),
    }));
  };
  const applyHubScope = (scope) => {
    setFilters((prev) => {
      const next = {
        ...prev,
        location_hub_ids: scope.location_hub_ids || [],
        child_location_hub_ids: scope.child_location_hub_ids || [],
        include_child_branches: scope.include_child_branches !== false,
      };
      const effective = new Set();
      const nextModes = modesFromHubFilters(
        hubTreeGroups,
        next.location_hub_ids,
        next.child_location_hub_ids,
        next.include_child_branches,
      );
      const byId = Object.fromEntries(nextModes.map((m) => [String(m.id), m]));
      hubTreeGroups.forEach((g) => {
        const m = byId[String(g.value)];
        if (!m || m.mode === 'none') return;
        if (m.mode === 'hub-only') effective.add(String(g.value));
        else if (m.mode === 'all') {
          effective.add(String(g.value));
          (g.children || []).forEach((c) => effective.add(String(c.value)));
        } else (m.selectedChildren || []).forEach((id) => effective.add(String(id)));
      });
      if (effective.size && prev.user_id) {
        const match = (users || []).find((u) => String(u.user_id) === String(prev.user_id));
        if (!match || !effective.has(String(match.location_hub_id || ''))) next.user_id = '';
      }
      if (effective.size && prev.agent_user_id) {
        const match = (users || []).find((u) => String(u.user_id) === String(prev.agent_user_id));
        if (!match || !effective.has(String(match.location_hub_id || ''))) next.agent_user_id = '';
      }
      return next;
    });
  };
  const isHubLocked = type === 'daily-branch' && isAgentUser && Boolean(user?.location_hub_id);

  const hubScopeFilter = (label = 'Location Hub') => (
    type === 'daily-branch' ? (
      <NestedHubDropdown
        label={label}
        groups={hubTreeGroups}
        selectedHubIds={selectedHubIds}
        selectedChildIds={selectedChildHubIds}
        includeChildren={includeChildBranches}
        onChange={applyHubScope}
        locked={isHubLocked}
      />
    ) : (
      <CheckboxDropdown
        label={label}
        emptyLabel="All Branches"
        options={flatBranchOptions}
        selected={selectedHubIds}
        locked={isHubLocked}
        onChange={(ids) => applyHubScope({
          location_hub_ids: ids,
          include_child_branches: false,
          child_location_hub_ids: [],
        })}
      />
    )
  );

  useEffect(() => {
    if (type !== 'daily-branch') return undefined;
    if (roleName !== 'Agent' && roleName !== 'Hub Admin') return undefined;
    const mineId = user?.location_hub_id != null ? String(user.location_hub_id) : '';
    if (!mineId) return undefined;
    setFilters((prev) => {
      const current = (prev.location_hub_ids || []).map(String).filter(Boolean);
      if (roleName === 'Agent') {
        if (current.length === 1 && current[0] === mineId) return prev;
        return {
          ...prev,
          location_hub_ids: [mineId],
          include_child_branches: true,
          child_location_hub_ids: [],
        };
      }
      if (current.length) return prev;
      return {
        ...prev,
        location_hub_ids: [mineId],
        include_child_branches: true,
        child_location_hub_ids: [],
      };
    });
    return undefined;
  }, [type, roleName, user?.location_hub_id]);

  const filterFields = (() => {
    switch (type) {
      case 'user':
        return (
          <>
            {hubScopeFilter('Branch')}
            <ReportSelect label="User" value={filters.user_id} onChange={(v) => setF('user_id', v)} placeholder="All Users" options={hubScopedUserOptions} />
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'location':
        return (
          <>
            {hubScopeFilter('Branch')}
            <CheckboxDropdown
              label="From Station"
              emptyLabel="All Stations"
              options={stationOptions}
              selected={(filters.from_station_ids || []).map(String)}
              onChange={(v) => setF('from_station_ids', v)}
            />
            <CheckboxDropdown
              label="To Station"
              emptyLabel="All Stations"
              options={stationOptions}
              selected={(filters.to_station_ids || []).map(String)}
              onChange={(v) => setF('to_station_ids', v)}
            />
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'vehicle':
        return (
          <>
            <ReportSelect label="Vehicle" value={filters.vehicle_id} onChange={(v) => setF('vehicle_id', v)} placeholder="All Vehicles" options={vehicleOptions} />
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'ml-sheet':
        return (
          <>
            <TextInput submit label="Dispatch ID" value={filters.ml_sheet_id} onChange={(v) => setF('ml_sheet_id', v)} placeholder="Leave blank for all" />
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
            <CheckboxDropdown label="Sender Locations" options={locationOptions} selected={filters.sender_locations || []} onChange={(v) => setF('sender_locations', v)} />
            <CheckboxDropdown label="Receiver Locations" options={locationOptions} selected={filters.receiver_locations || []} onChange={(v) => setF('receiver_locations', v)} />
          </>
        );
      case 'shipment':
      case 'business':
        return (
          <>
            <TextInput submit label="Shipment ID" value={filters.shipment_id} onChange={(v) => setF('shipment_id', v)} placeholder="Leave blank for all" />
            <CheckboxDropdown
              label="Status"
              emptyLabel="All Statuses"
              options={[
                { value: 'Booked', label: 'Booked' },
                { value: 'In Transit', label: 'In Transit' },
                { value: 'Dispatched', label: 'Dispatched' },
                { value: 'Received', label: 'Received' },
                { value: 'Delivered', label: 'Delivered' },
                { value: 'Returned', label: 'Returned' },
                { value: 'Cancelled', label: 'Cancelled' },
              ]}
              selected={Array.isArray(filters.status) ? filters.status : (filters.status ? [filters.status] : [])}
              onChange={(v) => setF('status', v)}
            />
            <TextInput submit label="GST Number" value={filters.gst_number || ''} onChange={(v) => setF('gst_number', v)} placeholder="Sender or Receiver GST" />
            <TextInput submit label="Sender Contact" value={filters.sender_contact || ''} onChange={(v) => setF('sender_contact', v)} placeholder="Sender phone number" />
            <TextInput submit label="Receiver Contact" value={filters.receiver_contact || ''} onChange={(v) => setF('receiver_contact', v)} placeholder="Receiver phone number" />
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
            <CheckboxDropdown label="Sender Locations" options={locationOptions} selected={filters.sender_locations || []} onChange={(v) => setF('sender_locations', v)} />
            <CheckboxDropdown label="Receiver Locations" options={locationOptions} selected={filters.receiver_locations || []} onChange={(v) => setF('receiver_locations', v)} />
            {type === 'business' && (
              <>
                <CheckboxDropdown
                  label="From Station"
                  emptyLabel="All Stations"
                  options={stationOptions}
                  selected={(filters.from_station_ids || []).map(String)}
                  onChange={(v) => setF('from_station_ids', v)}
                />
                <CheckboxDropdown
                  label="To Station"
                  emptyLabel="All Stations"
                  options={stationOptions}
                  selected={(filters.to_station_ids || []).map(String)}
                  onChange={(v) => setF('to_station_ids', v)}
                />
              </>
            )}
            {hubScopeFilter()}
            <CheckboxDropdown
              label="Payment Modes"
              options={[
                { value: 'paid', label: 'Paid' },
                { value: 'cash', label: 'Cash' }, { value: 'to_pay', label: 'To Pay' },
                { value: 'credit', label: 'Credit / On Account' }, { value: 'upi', label: 'UPI' },
                { value: 'bank_transfer', label: 'Bank Transfer' }, { value: 'cheque', label: 'Cheque' },
                { value: 'online', label: 'Online' },
              ]}
              selected={filters.payment_modes || []}
              onChange={(v) => setF('payment_modes', v)}
            />
            <CheckboxDropdown
              label="Shipment Client"
              emptyLabel="All Clients"
              options={clientOptions}
              selected={(filters.shipment_client_ids || []).map(String)}
              onChange={(v) => setF('shipment_client_ids', v)}
            />
            {!isAgentUser && (
              <ReportSelect label="Agent / User" value={filters.user_id} onChange={(v) => setF('user_id', v)} placeholder="All Agents" options={hubScopedUserOptions} />
            )}
            <ReportSelect label="Vehicle" value={filters.vehicle_id} onChange={(v) => setF('vehicle_id', v)} placeholder="All Vehicles" options={vehicleOptions} />
          </>
        );
      case 'daily-business':
        return (
          <>
            <FilterField>
              <label className={filterLabelClass}>From date</label>
              <input
                type="date"
                value={filters.from_date || ''}
                onChange={(e) => setF('from_date', e.target.value || getTodayIso())}
                className={filterDateInputClass}
              />
            </FilterField>
            <FilterField>
              <label className={filterLabelClass}>To date</label>
              <input
                type="date"
                value={filters.to_date || ''}
                onChange={(e) => setF('to_date', e.target.value || getTodayIso())}
                className={filterDateInputClass}
              />
            </FilterField>
            {hubScopeFilter('Location Hub')}
            <CheckboxDropdown
              label="From Location"
              emptyLabel="All Locations"
              options={stationOptions}
              selected={(filters.from_station_ids || []).map(String)}
              onChange={(v) => setF('from_station_ids', v)}
            />
            <CheckboxDropdown
              label="To Location"
              emptyLabel="All Locations"
              options={stationOptions}
              selected={(filters.to_station_ids || []).map(String)}
              onChange={(v) => setF('to_station_ids', v)}
            />
            <ReportSelect
              label="User (Agent)"
              value={filters.user_id || ''}
              onChange={(v) => setF('user_id', v)}
              placeholder="All Agents"
              options={agentOptions.length ? agentOptions : hubScopedUserOptions}
            />
          </>
        );
      case 'receivable':
        return (
          <>
            <FilterField>
              <label className={filterLabelClass}>Report Date</label>
              <input
                type="date"
                value={filters.report_date || ''}
                onChange={(e) => setF('report_date', e.target.value || getTodayIso())}
                className={filterDateInputClass}
              />
            </FilterField>
            {hubScopeFilter()}
          </>
        );
      case 'daily-branch':
        return (
          <>
            <FilterField>
              <label className={filterLabelClass}>From date</label>
              <input
                type="date"
                value={filters.from_date || filters.report_date || ''}
                onChange={(e) => setF('from_date', e.target.value || getTodayIso())}
                className={filterDateInputClass}
              />
            </FilterField>
            <FilterField>
              <label className={filterLabelClass}>To date</label>
              <input
                type="date"
                value={filters.to_date || filters.report_date || ''}
                onChange={(e) => {
                  const value = e.target.value || getTodayIso();
                  setF('to_date', value);
                  setF('report_date', value);
                }}
                className={filterDateInputClass}
              />
            </FilterField>
            {hubScopeFilter()}
            <ReportSelect
              label="Side"
              value={filters.report_side || 'both'}
              onChange={(v) => setF('report_side', v || 'both')}
              placeholder="Both"
              options={[
                { value: 'both', label: 'Both' },
                { value: 'booking', label: 'Booking' },
                { value: 'delivery', label: 'Delivery' },
              ]}
            />
            {!isAgentUser && (
              <ReportSelect
                label="User"
                value={filters.user_id || ''}
                onChange={(v) => setDailyBranchUser(v)}
                placeholder="All Users"
                options={dailyBranchUserOptions}
              />
            )}
          </>
        );
      case 'route-pl':
        return (
          <>
            <DateRangeRow
              label="Date range"
              fromValue={filters.from_date || ''}
              toValue={filters.to_date || ''}
              onFromChange={(v) => setF('from_date', v)}
              onToChange={(v) => setF('to_date', v)}
            />
            {hubScopeFilter()}
          </>
        );
      case 'receive':
      case 'delivery-event':
      case 'ewaybills':
      case 'onaccount-credit':
      case 'invoice-cancellation':
      case 'party-booking':
        return (
          <>
            {['receive', 'delivery-event'].includes(type) && (
              <TextInput submit label="Shipment ID" value={filters.shipment_id || ''} onChange={(v) => setF('shipment_id', v)} placeholder="Leave blank for all" />
            )}
            {hubScopeFilter()}
            <CheckboxDropdown
              label="Shipment Client"
              emptyLabel="All Clients"
              options={clientOptions}
              selected={(filters.shipment_client_ids || []).map(String)}
              onChange={(v) => setF('shipment_client_ids', v)}
            />
            <DateRangeRow label="Date range" fromValue={filters.from_date || ''} toValue={filters.to_date || ''} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'approved-transactions':
        return (
          <>
            {hubScopeFilter('Branch / Location')}
            <ReportSelect label="Category" value={filters.category || ''} onChange={(v) => setF('category', v)} placeholder="All Categories" options={expenseCategories} />
            <DateRangeRow label="Date range" fromValue={filters.from_date || ''} toValue={filters.to_date || ''} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'goods-balance':
      case 'branch-payable':
        return (
          <>
            {hubScopeFilter()}
          </>
        );
      case 'hub-branch-agents':
        return (
          <>
            {hubScopeFilter('Hub / Branch')}
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'expense':
        return (
          <>
            {hubScopeFilter('Branch / Location')}
            <ReportSelect label="Category" value={filters.category || ''} onChange={(v) => setF('category', v)} placeholder="All Categories" options={expenseCategories} />
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'on-contract':
      case 'vehicle-contract':
      case 'trip-contract':
        return (
          <>
            <TextInput submit label="Contract / Trip ID" value={filters.contract_id} onChange={(v) => setF('contract_id', v)} placeholder="Leave blank for all" />
            <ReportSelect label="Vehicle" value={filters.vehicle_id} onChange={(v) => setF('vehicle_id', v)} placeholder="All Vehicles" options={vehicleOptions} />
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'agent-tds':
        return (
          <>
            {!isAgentUser && (
              <ReportSelect label="Agent" value={filters.user_id || filters.agent_user_id} onChange={(v) => { setF('user_id', v); setF('agent_user_id', v); }} placeholder="All Agents" options={agentOptions} />
            )}
            {hubScopeFilter('Hub / Branch')}
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'commission':
        return (
          <>
            <ReportSelect
              label="Contracted Party"
              value={filters.contract_party_id}
              onChange={(v) => setF('contract_party_id', v)}
              placeholder="All Contracted Parties"
              options={contractPartyOptions}
            />
            {!isAgentUser && (
              <ReportSelect label="Agent" value={filters.agent_user_id} onChange={(v) => setF('agent_user_id', v)} placeholder="All Agents" options={agentOptions} />
            )}
            {hubScopeFilter('Hub / Branch')}
            <ReportSelect
              label="Status"
              value={filters.status}
              onChange={(v) => setF('status', v)}
              placeholder="All Status"
              options={[{ value: 'UNPAID', label: 'Unpaid' }, { value: 'PAID', label: 'Paid' }]}
            />
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'external-commission':
        return (
          <>
            <ReportSelect
              label="Vehicle Owner"
              value={filters.vehicle_owner_id}
              onChange={(v) => setF('vehicle_owner_id', v)}
              placeholder="All Vehicle Owners"
              options={(vehicleOwners || []).map((o) => ({
                value: o.vehicle_owner_id,
                label: o.owner_name || o.vehicle_owner_id,
              }))}
            />
            <CheckboxDropdown
              label="Booking Agents"
              options={(externalAgents.length
                ? externalAgents
                : (users || []).filter((u) => {
                  const role = typeof u.role === 'string' ? u.role : (u.role_name || u.role?.role_name || '');
                  return role === 'Agent';
                }).map((u) => ({ agent_id: u.user_id, agent_name: u.name || u.username }))
              ).map((a) => ({
                value: a.agent_id,
                label: a.agent_name || a.agent_id,
              }))}
              selected={filters.agent_user_ids || []}
              onChange={(v) => setF('agent_user_ids', v)}
            />
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'hr':
        return (
          <>
            <ReportSelect
              label="Report Type"
              value={filters.type}
              onChange={(v) => setF('type', v)}
              placeholder="Report Type"
              options={[
                { value: 'both', label: 'Attendance + Salary' },
                { value: 'attendance', label: 'Attendance Only' },
                { value: 'salary', label: 'Salary Only' },
              ]}
            />
            {!isAgentUser && (
              <ReportSelect label="User" value={filters.user_id} onChange={(v) => setF('user_id', v)} placeholder="All Users" options={userOptions} />
            )}
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
            <ReportSelect
              label="Month (Salary)"
              value={filters.month}
              onChange={(v) => setF('month', v)}
              placeholder="All Months"
              options={['january','february','march','april','may','june','july','august','september','october','november','december'].map((m) => ({
                value: m, label: m.charAt(0).toUpperCase() + m.slice(1),
              }))}
            />
            <ReportSelect
              label="Salary Status"
              value={filters.status}
              onChange={(v) => setF('status', v)}
              placeholder="All Status"
              options={[{ value: 'pending', label: 'Pending' }, { value: 'credited', label: 'Credited' }]}
            />
          </>
        );
      case 'gst-collection':
        return (
          <>
            <DateRangeRow
              label="Date range"
              fromValue={filters.from_date || ''}
              toValue={filters.to_date || ''}
              onFromChange={(v) => setF('from_date', v)}
              onToChange={(v) => setF('to_date', v)}
            />
            <FilterField>
              <label className={filterLabelClass}>Type</label>
              <select
                value={filters.collection_type || 'booking'}
                onChange={(e) => setF('collection_type', e.target.value)}
                className={filterInputClass}
              >
                <option value="booking">Booking</option>
                <option value="transaction">Transaction</option>
              </select>
            </FilterField>
            <FilterField>
              <label className={filterLabelClass}>GST</label>
              <select
                value={filters.gst_filter || 'gst'}
                onChange={(e) => setF('gst_filter', e.target.value)}
                className={filterInputClass}
              >
                <option value="gst">GST</option>
                <option value="non_gst">NON GST</option>
              </select>
            </FilterField>
          </>
        );
      default:
        return (
          <DateRangeRow label="Date range" fromValue={filters.from_date || ''} toValue={filters.to_date || ''} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
        );
    }
  })();

  return filterFields;
}
